import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutTemplate, Plus, CheckCircle2, Clock, AlertCircle, Loader2, Search, RefreshCw, Copy, Trash2, Check, Edit2, Lock, MessageSquare } from 'lucide-react';
import styles from './Templates.module.css';
import { useStore } from '../../../../context/StoreContext';
import { SMIIFY_API } from '../../../../config/api';

// Templates of the WhatsApp line in use. Hirsta / HQ templates can be used but not changed here.
const TEMPLATE_API = `${SMIIFY_API}/templates`;

const Templates = () => {
  const { activeStore } = useStore();
  const navigate = useNavigate();
  const isHQ = !activeStore || activeStore?.isHQ === true || (activeStore?._id || activeStore?.id) === 'global_hq';
  const storeId = isHQ ? 'global_hq' : String(activeStore?._id || activeStore?.id);
  const [filter, setFilter] = useState('All');
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ canManage: false, notice: '', line: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId }), [storeId]);
  const load = useCallback(async (manual) => {
    if (manual) setSyncing(true); else setLoading(true);
    setError('');
    try {
      const j = await fetch(TEMPLATE_API, { headers: headers() }).then((r) => r.json());
      if (!j.success) throw new Error(j.message);
      setList(j.data || []);
      setMeta({ canManage: j.canManage, notice: j.notice || '', line: j.line });
    } catch (e) { setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); } finally { setLoading(false); setSyncing(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const remove = async (name) => {
    if (!window.confirm(`Delete "${name}"? Meta removes it for good.`)) return;
    setDeleting(name);
    try {
      const j = await fetch(`${TEMPLATE_API}/delete?name=${encodeURIComponent(name)}`, { method: 'DELETE', headers: headers() }).then((r) => r.json());
      if (!j.success) throw new Error(j.message);
      setList((x) => x.filter((t) => t.name !== name));
    } catch (e) { setError(e.message); } finally { setDeleting(null); }
  };

  const shown = list.filter((t) => (filter === 'All' || String(t.category).toUpperCase() === filter.toUpperCase()) && (t.name.toLowerCase().includes(q.toLowerCase()) || String(t.id).includes(q)));
  const quality = (s) => ({ GREEN: ['#166534', '#dcfce7', 'High'], HIGH: ['#166534', '#dcfce7', 'High'], YELLOW: ['#854d0e', '#fef08a', 'Medium'], MEDIUM: ['#854d0e', '#fef08a', 'Medium'], RED: ['#991b1b', '#fee2e2', 'Low'], LOW: ['#991b1b', '#fee2e2', 'Low'] }[String(s).toUpperCase()] || ['#475569', '#f1f5f9', 'Not rated']);

  return (
    <div className={styles.templatesContainer}>
      <header className={styles.headerSection}>
        <div className={styles.headerLeft}>
          <h1><LayoutTemplate size={16} color="var(--p-600)" strokeWidth={2.5} /> Message templates</h1>
          <p>{meta.line ? <><MessageSquare size={11} style={{ verticalAlign: '-1px' }} /> {meta.line.label}</> : 'Templates approved by Meta'}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button type="button" onClick={() => load(true)} disabled={syncing} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
            <RefreshCw size={12} className={syncing ? styles.spin : ''} /> {syncing ? 'Syncing...' : 'Sync with Meta'}
          </button>
          {meta.canManage && <button type="button" className={styles.primaryBtn} onClick={() => navigate('/marketing/templates/create')}><Plus size={14} strokeWidth={3} /> Create template</button>}
        </div>
      </header>

      {meta.notice && <div style={{ margin: '0 0 12px', padding: '10px 14px', borderRadius: 10, background: '#f5f6ff', border: '1px solid #e6e8ff', color: '#331c74', fontSize: 12.5, fontWeight: 700, display: 'flex', gap: 8, alignItems: 'center' }}><Lock size={14} /> {meta.notice}</div>}

      <div className={styles.tableCard}>
        <div className={styles.filtersRow}>
          <div style={{ display: 'flex', gap: 6 }}>{['All', 'Marketing', 'Utility', 'Authentication'].map((x) => <button key={x} type="button" className={`${styles.filterBtn} ${filter === x ? styles.active : ''}`} onClick={() => setFilter(x)}>{x}</button>)}</div>
          <div style={{ position: 'relative', width: 240 }}>
            <Search size={12} color="#64748b" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input type="text" placeholder="Search name or ID..." value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '100%', padding: '6px 12px 6px 28px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 11, outline: 'none' }} />
          </div>
        </div>
        {error && <div style={{ margin: '10px 14px', padding: '8px 12px', background: '#fef2f2', borderLeft: '3px solid #ef4444', color: '#991b1b', fontSize: 11, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6 }}><AlertCircle size={14} /> {error}</div>}
        <div className={styles.tableWrapper}>
          <table className={styles.customTable}>
            <thead><tr><th>Template</th><th>Category</th><th>Language</th><th>Quality</th><th>Status</th><th style={{ textAlign: 'center' }}>Actions</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px 0' }}><Loader2 size={24} color="var(--p-600)" style={{ animation: 'spin 1s linear infinite' }} /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', color: '#64748b' }}><LayoutTemplate size={36} color="#cbd5e1" /><h3 style={{ fontSize: 14, color: '#334155', margin: '10px 0 4px' }}>No templates</h3><p style={{ fontSize: 11 }}>{q ? 'Try another search.' : meta.canManage ? 'Create your first template.' : 'Ask the line owner to add templates.'}</p></div></td></tr>
              ) : shown.map((t) => {
                const [qc, qb, ql] = quality(t.quality_score?.score);
                const editable = !t.readOnly && t.status !== 'PENDING' && t.status !== 'IN_APPEAL';
                return (
                  <tr key={t.id}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span className={styles.fw600} style={{ color: '#0f172a', fontSize: 12 }}>{t.name}{t.readOnly && <Lock size={10} style={{ marginLeft: 5, color: '#94a3b8' }} />}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#64748b' }}>ID {t.id}
                          <button type="button" onClick={() => { navigator.clipboard.writeText(t.id); setCopied(t.id); setTimeout(() => setCopied(null), 1500); }} title="Copy ID" style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied === t.id ? '#16a34a' : '#94a3b8', display: 'flex' }}>{copied === t.id ? <Check size={10} /> : <Copy size={10} />}</button>
                        </div>
                        {t.status === 'REJECTED' && t.rejectedReason && <span style={{ fontSize: 10.5, color: '#be1025' }}>Rejected: {String(t.rejectedReason).toLowerCase().replace(/_/g, ' ')}</span>}
                      </div>
                    </td>
                    <td><span className={styles.catBadge}>{t.category}</span></td>
                    <td style={{ fontSize: 11 }}>{t.lang}</td>
                    <td><span style={{ padding: '3px 8px', background: qb, color: qc, borderRadius: 6, fontSize: 10, fontWeight: 600 }}>{ql}</span></td>
                    <td>
                      <span className={`${styles.statusBadge} ${t.status === 'APPROVED' ? styles.statusApproved : t.status === 'PENDING' ? styles.statusPending : styles.statusRejected}`}>
                        {t.status === 'APPROVED' ? <CheckCircle2 size={10} strokeWidth={3} /> : t.status === 'PENDING' ? <Clock size={10} strokeWidth={3} /> : <AlertCircle size={10} strokeWidth={3} />} {t.status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {t.readOnly ? <span style={{ fontSize: 11, color: '#94a3b8' }}>Use only</span> : (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                          <button type="button" onClick={() => navigate(`/marketing/templates/edit/${t.id}`, { state: { template: t } })} disabled={!editable} title={editable ? 'Edit' : 'A template under review cannot be edited'} className={styles.actionBtn} style={{ color: editable ? '#673de6' : '#cbd5e1' }}><Edit2 size={14} /></button>
                          <button type="button" onClick={() => remove(t.name)} disabled={deleting === t.name} title="Delete" className={styles.actionBtn} style={{ color: '#ef4444' }}>{deleting === t.name ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={14} />}</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Templates;
